import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useRef } from 'react';
import { adminService } from '../services/adminService';
import LoadingSpinner from '../components/LoadingSpinner';

const AdminPage = () => {
  const queryClient = useQueryClient();
  const toast = useRef(null);
  
  const [activeTab, setActiveTab] = useState(0);
  const [userDialogVisible, setUserDialogVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'VIEWER',
  });

  const [filters, setFilters] = useState({
    search: '',
    role: '',
    page: 1,
    limit: 10,
  });

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['admin-users', filters],
    queryFn: () => adminService.getUsers(filters),
  });

  const { data: auditLogsData, isLoading: auditLogsLoading } = useQuery({
    queryKey: ['admin-audit-logs'],
    queryFn: () => adminService.getAuditLogs({ limit: 50 }),
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => adminService.getSystemStats(),
  });

  const createUserMutation = useMutation({
    mutationFn: adminService.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users']);
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'User created successfully!',
      });
      setUserDialogVisible(false);
      resetUserForm();
    },
    onError: (error) => {
      toast.current.show({
        severity: 'error',
        summary: 'Create Failed',
        detail: error.message,
      });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }) => adminService.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users']);
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'User updated successfully!',
      });
      setUserDialogVisible(false);
      resetUserForm();
    },
    onError: (error) => {
      toast.current.show({
        severity: 'error',
        summary: 'Update Failed',
        detail: error.message,
      });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: adminService.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['admin-users']);
      toast.current.show({
        severity: 'success',
        summary: 'Success',
        detail: 'User deleted successfully!',
      });
    },
    onError: (error) => {
      toast.current.show({
        severity: 'error',
        summary: 'Delete Failed',
        detail: error.message,
      });
    },
  });

  const roleOptions = [
    { label: 'Viewer', value: 'VIEWER' },
    { label: 'Editor', value: 'EDITOR' },
    { label: 'Admin', value: 'ADMIN' },
  ];

  const resetUserForm = () => {
    setUserForm({
      username: '',
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'VIEWER',
    });
    setEditingUser(null);
  };

  const handleCreateUser = () => {
    resetUserForm();
    setUserDialogVisible(true);
  };

  const handleEditUser = (user) => {
    setUserForm({
      username: user.username,
      email: user.email,
      password: '',
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
    });
    setEditingUser(user);
    setUserDialogVisible(true);
  };

  const handleSaveUser = () => {
    if (editingUser) {
      const { password, ...updateData } = userForm;
      updateUserMutation.mutate({
        id: editingUser.id,
        data: updateData,
      });
    } else {
      createUserMutation.mutate(userForm);
    }
  };

  const handleDeleteUser = (user) => {
    deleteUserMutation.mutate(user.id);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const userActionBodyTemplate = (rowData) => {
    return (
      <div className="flex gap-1">
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          tooltip="Edit User"
          onClick={() => handleEditUser(rowData)}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-sm p-button-danger"
          tooltip="Delete User"
          onClick={() => handleDeleteUser(rowData)}
        />
      </div>
    );
  };

  const users = usersData?.users || [];
  const auditLogs = auditLogsData?.auditLogs || [];
  const stats = statsData || {};

  if (usersLoading || auditLogsLoading || statsLoading) {
    return <LoadingSpinner message="Loading admin panel..." />;
  }

  return (
    <div className="grid">
      <div className="col-12">
        <Card>
          <div className="flex align-items-center justify-content-between mb-4">
            <h2 className="m-0">Admin Panel</h2>
          </div>

          <Toast ref={toast} />
          <ConfirmDialog />

          <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
            {/* System Statistics Tab */}
            <TabPanel header="Statistics">
              <div className="grid">
                <div className="col-12 md:col-3">
                  <Card>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary mb-2">
                        {stats.users?.total || 0}
                      </div>
                      <div className="text-muted">Total Users</div>
                    </div>
                  </Card>
                </div>
                <div className="col-12 md:col-3">
                  <Card>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-500 mb-2">
                        {stats.documents?.total || 0}
                      </div>
                      <div className="text-muted">Total Documents</div>
                    </div>
                  </Card>
                </div>
                <div className="col-12 md:col-3">
                  <Card>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-orange-500 mb-2">
                        {stats.documents?.published || 0}
                      </div>
                      <div className="text-muted">Published</div>
                    </div>
                  </Card>
                </div>
                <div className="col-12 md:col-3">
                  <Card>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-500 mb-2">
                        {stats.verifications?.total || 0}
                      </div>
                      <div className="text-muted">Total Verifications</div>
                    </div>
                  </Card>
                </div>
              </div>

              <div className="grid mt-4">
                <div className="col-12 md:col-6">
                  <Card>
                    <h3 className="mt-0 mb-3">Users by Role</h3>
                    <div className="flex flex-column gap-2">
                      {Object.entries(stats.users?.byRole || {}).map(([role, count]) => (
                        <div key={role} className="flex justify-content-between">
                          <span>{role}</span>
                          <span className="font-bold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
                <div className="col-12 md:col-6">
                  <Card>
                    <h3 className="mt-0 mb-3">Documents by Status</h3>
                    <div className="flex flex-column gap-2">
                      {Object.entries(stats.documents?.byStatus || {}).map(([status, count]) => (
                        <div key={status} className="flex justify-content-between">
                          <span>{status}</span>
                          <span className="font-bold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              </div>
            </TabPanel>

            {/* User Management Tab */}
            <TabPanel header="User Management">
              <div className="flex justify-content-between mb-4">
                <div className="flex gap-2">
                  <InputText
                    placeholder="Search users..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  />
                  <Dropdown
                    placeholder="Filter by role"
                    options={[{ label: 'All Roles', value: '' }, ...roleOptions]}
                    value={filters.role}
                    onChange={(e) => setFilters(prev => ({ ...prev, role: e.value }))}
                  />
                </div>
                <Button
                  label="Create User"
                  icon="pi pi-plus"
                  onClick={handleCreateUser}
                />
              </div>

              <DataTable
                value={users}
                paginator
                rows={filters.limit}
                totalRecords={usersData?.pagination?.total || 0}
                emptyMessage="No users found"
                className="p-datatable-sm"
              >
                <Column field="username" header="Username" sortable />
                <Column field="email" header="Email" sortable />
                <Column field="firstName" header="First Name" sortable />
                <Column field="lastName" header="Last Name" sortable />
                <Column field="role" header="Role" sortable />
                <Column
                  field="isActive"
                  header="Status"
                  body={(rowData) => (
                    <span className={`badge ${rowData.isActive ? 'badge-published' : 'badge-draft'}`}>
                      {rowData.isActive ? 'Active' : 'Inactive'}
                    </span>
                  )}
                />
                <Column
                  field="createdAt"
                  header="Created"
                  body={(rowData) => formatDate(rowData.createdAt)}
                  sortable
                />
                <Column
                  header="Actions"
                  body={userActionBodyTemplate}
                  style={{ width: '120px' }}
                />
              </DataTable>
            </TabPanel>

            {/* Audit Logs Tab */}
            <TabPanel header="Audit Logs">
              <DataTable
                value={auditLogs}
                paginator
                rows={20}
                emptyMessage="No audit logs found"
                className="p-datatable-sm"
              >
                <Column field="action" header="Action" sortable />
                <Column field="resource" header="Resource" sortable />
                <Column
                  field="user.username"
                  header="User"
                  body={(rowData) => rowData.user?.username || 'System'}
                />
                <Column
                  field="ipAddress"
                  header="IP Address"
                  sortable
                />
                <Column
                  field="success"
                  header="Status"
                  body={(rowData) => (
                    <span className={`badge ${rowData.success ? 'badge-published' : 'badge-revoked'}`}>
                      {rowData.success ? 'Success' : 'Failed'}
                    </span>
                  )}
                />
                <Column
                  field="createdAt"
                  header="Date"
                  body={(rowData) => formatDate(rowData.createdAt)}
                  sortable
                />
              </DataTable>
            </TabPanel>
          </TabView>
        </Card>
      </div>

      {/* User Dialog */}
      <Dialog
        header={editingUser ? 'Edit User' : 'Create User'}
        visible={userDialogVisible}
        style={{ width: '50vw' }}
        onHide={() => setUserDialogVisible(false)}
      >
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block text-sm font-medium mb-2">Username *</label>
              <InputText
                value={userForm.username}
                onChange={(e) => setUserForm(prev => ({ ...prev, username: e.target.value }))}
                className="w-full"
                required
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block text-sm font-medium mb-2">Email *</label>
              <InputText
                value={userForm.email}
                onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                className="w-full"
                type="email"
                required
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block text-sm font-medium mb-2">First Name *</label>
              <InputText
                value={userForm.firstName}
                onChange={(e) => setUserForm(prev => ({ ...prev, firstName: e.target.value }))}
                className="w-full"
                required
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block text-sm font-medium mb-2">Last Name *</label>
              <InputText
                value={userForm.lastName}
                onChange={(e) => setUserForm(prev => ({ ...prev, lastName: e.target.value }))}
                className="w-full"
                required
              />
            </div>
          </div>
          {!editingUser && (
            <div className="col-12 md:col-6">
              <div className="field">
                <label className="block text-sm font-medium mb-2">Password *</label>
                <InputText
                  value={userForm.password}
                  onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full"
                  type="password"
                  required
                />
              </div>
            </div>
          )}
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block text-sm font-medium mb-2">Role *</label>
              <Dropdown
                value={userForm.role}
                options={roleOptions}
                onChange={(e) => setUserForm(prev => ({ ...prev, role: e.value }))}
                className="w-full"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-content-end gap-2 mt-4">
          <Button
            label="Cancel"
            icon="pi pi-times"
            className="p-button-text"
            onClick={() => setUserDialogVisible(false)}
          />
          <Button
            label={editingUser ? 'Update' : 'Create'}
            icon="pi pi-check"
            onClick={handleSaveUser}
            loading={createUserMutation.isPending || updateUserMutation.isPending}
          />
        </div>
      </Dialog>
    </div>
  );
};

export default AdminPage;